import { forwardRef, Fragment, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { defaultRangeExtractor, useVirtual } from 'react-virtual';
import cx from 'classnames';
import { useRefArray } from '../../hooks/useRefArray';
import { useIsRTL } from '../../locale';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { setRef } from '../../util/ref-util';
import { postfixTestId } from '../../util/testId';
import { ContextMenu } from '../ContextMenu';
import { TileDragPreview, TilesContextProvider, useTileDroppable } from '../Tile';
import { ZeroState } from '../ZeroState';
import bundle from './NovaComponents-TiledLayout-gui-icu.properties';
import './TiledLayout.css';
import { useDirectSpeech } from '../DirectSpeech';
import presentationTileCn from '../PresentationTile/PresentationTile.css.json';
import tileCn from '../Tile/Tile.css.json';
import cn from './TiledLayout.css.json';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useTextLocalizer = createUseTextLocalizer(bundle);
const LayoutTile = props => {
  const localize = useTextLocalizer();
  const reorderSection = {
    items: [{
      text: localize('moveTile.label'),
      key: 'moveTile'
    }]
  };
  let contextMenuSections = props.contextMenuProps?.sections ?? [];
  contextMenuSections = props.dndProps ? contextMenuSections.concat(reorderSection) : contextMenuSections;
  return /*#__PURE__*/_jsx("div", {
    "data-index": props.index,
    className: cx('tileWrapper', props.disableVariableWidth ? undefined : cn.variableWidth),
    onMouseDown: _event => {
      props.setFocusedIndex(props.index);
      props.setInReorderMode(false);
    },
    children: /*#__PURE__*/_jsx(ContextMenu, {
      ...props.contextMenuProps,
      sections: contextMenuSections,
      onItemSelected: (indices, key) => {
        if (key === 'moveTile') {
          props.setInReorderMode(true);
        }
        props.contextMenuProps?.onItemSelected?.(indices, key);
      },
      children: props.renderTile(props.index) ?? /*#__PURE__*/_jsx("div", {
        style: {
          height: props.tileHeight
        }
      })
    }, `tileMenu-${props.index}`)
  });
};
const EmptyTile = () => {
  // empty tiles are used as placeholders for the last row of tiles
  // if the number of tiles was not evenly divisible by the number of tiles per row
  return /*#__PURE__*/_jsx("div", {
    className: cn.variableWidth
  });
};
const LayoutRow = props => {
  const rowIndex = props.virtualRow.index;
  const start = props.virtualRow.start;
  const measureRef = props.virtualRow.measureRef;
  const cellsInRow = [];
  const tileIndex = rowIndex * props.tilesPerRow;
  for (let i = tileIndex; i < tileIndex + props.tilesPerRow; i++) {
    if (i >= props.length) {
      if (!props.disableVariableWidth) {
        cellsInRow.push( /*#__PURE__*/_jsx(EmptyTile, {}, `placeholderTile-${i}`));
      }
    } else {
      cellsInRow.push( /*#__PURE__*/_jsx(LayoutTile, {
        index: i,
        length: props.length,
        tilesPerRow: props.tilesPerRow,
        renderTile: props.renderTile,
        tileHeight: props.tileHeight,
        setFocusedIndex: props.setFocusedIndex,
        inReorderMode: props.inReorderMode,
        setInReorderMode: props.setInReorderMode,
        disableVariableWidth: props.disableVariableWidth,
        dndProps: props.dndProps,
        contextMenuProps: props.contextMenuProps
      }, `tileWrapper-${i}`));
    }
  }
  return /*#__PURE__*/_jsx("div", {
    style: {
      position: 'absolute',
      top: 0,
      left: 0,
      transform: `translateY(${start}px)`,
      width: '100%'
    },
    role: "row",
    "aria-rowindex": rowIndex + 1,
    ref: measureRef ?? undefined,
    className: cn.rowContainer,
    children: cellsInRow
  });
};
export const TiledLayout = /*#__PURE__*/forwardRef(function TiledLayout({
  length,
  renderTile,
  tileClassname = tileCn.tile,
  width,
  height,
  tilesContainerRef,
  testId,
  disableInteractiveMode = false,
  selectionMode = 'none',
  selectedTiles,
  getTileKey,
  onTileSelect,
  dndProps,
  contextMenuProps,
  zeroStateText,
  ariaLabel,
  disableVariableWidth,
  apiRef
}, ref) {
  const localize = useTextLocalizer();
  const internalTilesContainerRef = useRef(null);
  const {
    refs: tileRefs,
    createSetRef: createTileRef
  } = useRefArray();
  const parentRef = useRef(null);
  const rangeRef = useRef({
    start: 0,
    end: 0
  });
  const [currentTileIndex, setCurrentTileIndex] = useState(0);
  const [inReorderMode, setInReorderMode] = useState(false);
  const currentTileRef = tileRefs[currentTileIndex];
  const hasTileRef = !!currentTileRef;
  // the following flag will tell us whether the tiles are ready
  // to be measured and we can use this in the useMemo below
  const clientWidth = parentRef.current?.clientWidth ? parentRef.current?.clientWidth : 600;
  const defaultTileSize = 120;
  const tileWrapper = currentTileRef?.closest('.tileWrapper');

  // set the hasClientWidth flag when tile refs are set
  const hasClientWidth = useMemo(() => {
    return !!parentRef.current?.clientWidth;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasTileRef]);

  // set initialTileWidth and initialTileHeight when the TiledLayout and tiles have width to measure
  const [initialTileWidth, initialTileHeight] = useMemo(() => {
    // get the initial offset width and height of the tile's wrapper div
    const width = tileWrapper?.offsetWidth ?? defaultTileSize;
    const height = tileWrapper?.offsetHeight ?? defaultTileSize;
    return [width, height];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasClientWidth]);
  const tilesPerRow = Math.max(Math.floor(clientWidth / initialTileWidth), 1);
  const count = Math.ceil(length / tilesPerRow);
  const ignoreFocus = useRef(true);
  const isRTL = useIsRTL();
  const zeroStateRef = useRef(null);

  // To check if a tile is dragged over the container so that it can be added
  const {
    ref: containerDropRef
  } = useTileDroppable({
    allowDrop: dndProps?.allowDrop,
    allowDropDndPrefixes: dndProps?.allowDropDndPrefixes,
    dndPrefix: dndProps?.dndPrefix
  });
  const isSelected = useCallback(index => {
    if (index === undefined) return false;
    // if the selection mode is none or there are no selected tiles
    // return false
    if (selectionMode === 'none' || !selectedTiles) return false;

    // if there is no key for this tile, return false
    const tileKey = getTileKey?.(index);
    if (!tileKey) return false;

    // if this is single selection mode, only select the first tile
    // in the set of selected tiles
    if (selectionMode === 'single') {
      const [firstKey] = selectedTiles;
      return firstKey === tileKey;
    }

    // this is desktop or multiple selection mode
    // so see if this tile is in the set of selected tiles
    return selectedTiles.has(tileKey);
  }, [selectionMode, selectedTiles, getTileKey]);
  const itemVirtualizer = useVirtual({
    size: count,
    parentRef,
    estimateSize: useCallback(() => initialTileHeight, [initialTileHeight]),
    rangeExtractor: useCallback(range => {
      rangeRef.current = range;
      return defaultRangeExtractor(range);
    }, [])
  });
  const range = rangeRef.current.end - rangeRef.current.start;
  const rowsPerPage = range === 0 ? 10 : range;
  const apis = {
    scrollToIndex: (index, options) => {
      const rowIndex = Math.floor(index / tilesPerRow);
      itemVirtualizer.scrollToIndex(rowIndex, options);
    },
    setFocusedIndex: index => {
      ignoreFocus.current = false;
      setCurrentTileIndex(index);
    },
    setInReorderMode: reorderMode => {
      setInReorderMode(reorderMode);
    },
    createTileRef: index => {
      const ref = createTileRef(index);
      return ref;
    }
  };
  useImperativeHandle(apiRef, () => apis);
  useEffect(() => {
    if (currentTileIndex >= length) setCurrentTileIndex(0);
    // if the current index or the array of virtual items changes,
    // set focus to the new index
    if (!ignoreFocus.current) {
      // focus to the zero state if it turns to be no items (such as drill down or the last item is removed)
      if (length === 0) zeroStateRef.current?.focus();else tileRefs[currentTileIndex]?.focus();
    }
  }, [tileRefs, currentTileRef, currentTileIndex, itemVirtualizer.virtualItems, length]);
  const bottomBumperFocusHandler = event => {
    const focusFromBelow = !containerDropRef.current.contains(event.relatedTarget);
    if (focusFromBelow) {
      const rowIndex = Math.floor(currentTileIndex / tilesPerRow);
      itemVirtualizer.scrollToIndex(rowIndex);
      tileRefs[currentTileIndex]?.focus();
    } else {
      // when leaving the TiledLayout, set ignoreFocus back to true
      // so TiledLayout won't grab focus if the tiles change
      ignoreFocus.current = true;
    }
  };
  const topBumperFocusHandler = event => {
    const focusFromAbove = !containerDropRef.current.contains(event.relatedTarget);
    if (focusFromAbove) {
      const rowIndex = Math.floor(currentTileIndex / tilesPerRow);
      itemVirtualizer.scrollToIndex(rowIndex);
      tileRefs[currentTileIndex]?.focus();
    } else {
      // when leaving the TiledLayout, set ignoreFocus back to true
      // so TiledLayout won't grab focus if the tiles change
      ignoreFocus.current = true;
    }
  };
  const bottomBumperRef = useRef(null);
  const topBumperRef = useRef(null);
  const topBumperId = postfixTestId(testId, 'topBumper');
  const bottomBumperId = postfixTestId(testId, 'bottomBumper');
  const reorderTiles = (oldIndex, newIndex) => {
    if (oldIndex === -1 && newIndex === -1) {
      // exit reorder mode
      setInReorderMode(false);
      return;
    }
    // otherwise reorder the tiles and set focus to the new index
    dndProps?.reorderTiles?.(oldIndex, newIndex);
    setCurrentTileIndex(newIndex);
  };
  const directSpeaker = useDirectSpeech({
    mode: 'assertive'
  });
  const handleKeyDown = useVirtualKeyDownHandler({
    length,
    rowsPerPage,
    scrollToIndex: itemVirtualizer.scrollToIndex,
    currentTileIndex,
    tilesPerRow,
    setFocusedIndex: setCurrentTileIndex,
    reorderTiles,
    inReorderMode,
    onTileSelect,
    contextMenuProps,
    ignoreFocus,
    topBumperRef,
    bottomBumperRef,
    rangeRef,
    isRTL,
    tileClassname,
    directSpeaker,
    localize
  });
  const hideCheckbox = selectionMode !== 'multiple';
  const onSelect = info => {
    onTileSelect?.(info);
  };
  const [calculatedAriaLabel, setCalculatedAriaLabel] = useState('');
  useEffect(() => {
    setCalculatedAriaLabel(getTiledLayoutA11yInfo(localize, {
      selectionMode,
      ariaLabel,
      disableInteractiveMode
    }, inReorderMode).calculatedAriaLabel);
  }, [inReorderMode, selectionMode, ariaLabel, disableInteractiveMode, localize]);
  return /*#__PURE__*/_jsxs(TilesContextProvider, {
    value: {
      hideCheckbox,
      disableInteractiveMode,
      isGridCell: true,
      selectionMode: selectionMode === 'multiple' ? 'multi' : selectionMode,
      onSelect,
      isSelected,
      createTileRef
    },
    children: [/*#__PURE__*/_jsx("div", {
      "data-testid": testId,
      ref: element => {
        setRef(ref, element);
        setRef(containerDropRef, element);
      },
      className: cn.tiledLayout,
      style: {
        width: width ?? '100%',
        height: height ?? '100%'
      },
      children: length > 0 ? /*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx("div", {
          ref: topBumperRef,
          tabIndex: 0,
          onFocus: topBumperFocusHandler,
          "data-testid": topBumperId
        }), /*#__PURE__*/_jsx("div", {
          className: cn.content,
          role: "grid",
          ref: parentRef,
          "aria-rowcount": count,
          "aria-label": calculatedAriaLabel,
          "aria-multiselectable": selectionMode === 'multiple' || selectionMode === 'desktop' ? 'true' : undefined,
          children: /*#__PURE__*/_jsx("div", {
            ref: tilesContainerRef ?? internalTilesContainerRef,
            className: cn.tilesContainer,
            onKeyDown: handleKeyDown,
            onMouseDown: () => {
              ignoreFocus.current = true;
              setInReorderMode(false);
            },
            style: {
              height: itemVirtualizer.totalSize,
              width: '100%',
              position: 'relative'
            },
            children: itemVirtualizer.virtualItems.map(virtualRow => /*#__PURE__*/_jsx(LayoutRow, {
              length: length,
              virtualRow: virtualRow,
              tilesPerRow: tilesPerRow,
              tileHeight: initialTileHeight,
              renderTile: renderTile,
              setFocusedIndex: setCurrentTileIndex,
              inReorderMode: inReorderMode,
              setInReorderMode: setInReorderMode,
              disableVariableWidth: disableVariableWidth,
              dndProps: dndProps,
              contextMenuProps: contextMenuProps
            }, `row-${virtualRow.index}`))
          })
        }), /*#__PURE__*/_jsx("div", {
          ref: bottomBumperRef,
          tabIndex: 0,
          onFocus: bottomBumperFocusHandler,
          "data-testid": bottomBumperId
        })]
      }) : /*#__PURE__*/_jsx(ZeroState, {
        ref: zeroStateRef,
        className: cn.content,
        text: zeroStateText ?? localize('noItems.msg'),
        style: {
          height: '100%'
        }
      })
    }), dndProps && /*#__PURE__*/_jsx(TileDragPreview, {})]
  });
});
const getTiledLayoutA11yInfo = (localize, {
  selectionMode = 'multiple',
  ariaLabel,
  disableInteractiveMode
}, inReorderMode) => {
  const ariaLabels = [];
  ariaLabels.push(ariaLabel ?? localize('tiledItems.aria'));
  if (selectionMode === 'single') {
    ariaLabels.push(localize('selectionMode.single.aria'));
  } else if (selectionMode === 'multiple' || selectionMode === 'desktop') {
    ariaLabels.push(localize('selectionMode.multi.aria'));
  }
  if (inReorderMode) {
    ariaLabels.push(localize('reorderMode.hint.aria'));
  } else if (!disableInteractiveMode) {
    ariaLabels.push(localize('interactiveMode.hint.aria'));
  }
  return {
    calculatedAriaLabel: ariaLabels.join(', ')
  };
};
const useVirtualKeyDownHandler = ({
  length,
  rowsPerPage,
  scrollToIndex,
  currentTileIndex,
  tilesPerRow,
  setFocusedIndex,
  reorderTiles,
  inReorderMode,
  onTileSelect,
  ignoreFocus,
  topBumperRef,
  bottomBumperRef,
  rangeRef,
  isRTL,
  tileClassname,
  directSpeaker,
  localize
}) => {
  return useCallback(event => {
    // if focus is anywhere other than a Tile in this TiledLayout, do nothing
    if (!document.activeElement?.classList.contains(tileClassname) && !document.activeElement?.classList.contains(presentationTileCn.tile)) return;
    ignoreFocus.current = false;
    const selectTile = index => {
      onTileSelect?.({
        selectionArea: 'tile',
        index,
        altKey: event.altKey,
        ctlKey: event.ctrlKey,
        metaKey: event.metaKey,
        shiftKey: event.shiftKey
      });
    };
    if (length <= 0) {
      return;
    }
    const maxIndex = length - 1;
    const maxRowIndex = Math.ceil(length / tilesPerRow) - 1;
    let newIndex;
    let newRowIndex;
    const currentRowIndex = Math.floor(currentTileIndex / tilesPerRow);
    const endRowIndex = rangeRef?.current?.end ?? Math.floor(length / tilesPerRow);
    const startRowIndex = rangeRef?.current?.start ?? 0;
    let rowDiff = 0;

    // in reorder mode
    if (inReorderMode) {
      switch (event.key) {
        case isRTL ? 'ArrowRight' : 'ArrowLeft':
          reorderTiles(currentTileIndex, currentTileIndex - 1);
          break;
        case isRTL ? 'ArrowLeft' : 'ArrowRight':
          reorderTiles(currentTileIndex, currentTileIndex + 1);
          break;
        case 'ArrowUp':
          reorderTiles(currentTileIndex, currentTileIndex - tilesPerRow);
          break;
        case 'ArrowDown':
          reorderTiles(currentTileIndex, currentTileIndex + tilesPerRow);
          break;
        case 'Home':
          reorderTiles(currentTileIndex, 0);
          break;
        case 'End':
          reorderTiles(currentTileIndex, maxIndex);
          break;
        case 'Enter':
          // exit reorder mode by calling reorderTiles with -1
          reorderTiles(-1, -1);
          directSpeaker?.notify(localize('moveComplete.msg'));
          break;
      }
      return;
    }

    // not in reorder mode
    switch (event.key) {
      case 'Tab':
        if (!event.shiftKey) {
          bottomBumperRef.current?.focus({
            preventScroll: true
          });
        } else {
          topBumperRef.current?.focus();
        }
        break;
      case isRTL ? 'ArrowRight' : 'ArrowLeft':
        if (currentTileIndex > 0) {
          newIndex = currentTileIndex - 1;
          newRowIndex = Math.floor(newIndex / tilesPerRow);
          if (newRowIndex < startRowIndex) {
            scrollToIndex(newRowIndex);
          }
          setFocusedIndex(newIndex);
          if (event.shiftKey) selectTile(newIndex);
        }
        event.preventDefault();
        break;
      case isRTL ? 'ArrowLeft' : 'ArrowRight':
        if (currentTileIndex < length - 1) {
          newIndex = currentTileIndex + 1;
          newRowIndex = Math.floor(newIndex / tilesPerRow);
          if (newRowIndex > endRowIndex) {
            scrollToIndex(newRowIndex, {
              align: 'end'
            });
          }
          setFocusedIndex(newIndex);
          if (event.shiftKey) selectTile(newIndex);
        }
        event.preventDefault();
        break;
      case 'ArrowDown':
        newIndex = currentTileIndex + tilesPerRow < length ? currentTileIndex + tilesPerRow : length - 1;
        scrollToIndex(Math.floor(newIndex / tilesPerRow), {
          align: 'end'
        });
        setFocusedIndex(newIndex);
        if (event.shiftKey) {
          selectTile(newIndex);
        }
        event.preventDefault();
        break;
      case 'ArrowUp':
        newIndex = currentTileIndex - tilesPerRow > 0 ? currentTileIndex - tilesPerRow : 0;
        scrollToIndex(Math.floor(newIndex / tilesPerRow));
        setFocusedIndex(newIndex);
        if (event.shiftKey) {
          selectTile(newIndex);
        }
        event.preventDefault();
        break;
      case 'PageDown':
        {
          // if you're already on the last row, just return
          if (currentTileIndex === maxIndex || currentRowIndex >= maxRowIndex) return;
          // plan to go down one page of rows, unless you're on the last page
          newRowIndex = Math.min(endRowIndex, currentRowIndex + rowsPerPage);
          // if you're on the last row of the current page, scroll one page
          // otherwise go to the last row on the current page
          if (currentRowIndex === endRowIndex) newRowIndex = currentRowIndex + rowsPerPage;else newRowIndex = endRowIndex;
          // see how many rows you need to move down
          rowDiff = newRowIndex - currentRowIndex;
          // calculate the new tile index to focus based on the number of rows moved
          newIndex = Math.min(currentTileIndex + rowDiff * tilesPerRow, maxIndex);
          // scroll to the new row and set focus
          scrollToIndex(Math.floor(newIndex / tilesPerRow), {
            align: 'end'
          });
          setFocusedIndex(newIndex);
          event.preventDefault();
          break;
        }
      case 'PageUp':
        // if you're already on the first row, just return
        if (currentTileIndex === 0 || currentRowIndex === 0) return;
        // plan to go up one page of rows, unless you're on the first page
        newRowIndex = Math.max(startRowIndex, currentRowIndex - rowsPerPage);
        // if you're on the first row of the current page, scroll one page
        // otherwise go to the first row on the current page
        if (currentRowIndex === startRowIndex) newRowIndex = currentRowIndex - rowsPerPage;else newRowIndex = startRowIndex;
        // see how many rows you need to move up
        rowDiff = currentRowIndex - newRowIndex;
        // calculate the new tile index to focus based on the number of rows moved
        newIndex = Math.max(currentTileIndex - rowDiff * tilesPerRow, 0);
        // scroll to the new row and set focus
        scrollToIndex(Math.floor(newIndex / tilesPerRow));
        setFocusedIndex(newIndex);
        event.preventDefault();
        break;
      case 'End':
        if (event.ctrlKey) {
          scrollToIndex(maxRowIndex);
          setFocusedIndex(maxIndex);
          break;
        }
        newIndex = currentRowIndex * tilesPerRow + tilesPerRow - 1;
        setFocusedIndex(newIndex);
        event.preventDefault();
        break;
      case 'Home':
        if (event.ctrlKey) {
          scrollToIndex(0);
          setFocusedIndex(0);
          break;
        }
        newIndex = currentRowIndex * tilesPerRow;
        setFocusedIndex(newIndex);
        event.preventDefault();
        break;
    }
  }, [length, setFocusedIndex, currentTileIndex, scrollToIndex, rowsPerPage, ignoreFocus, topBumperRef, bottomBumperRef, isRTL, onTileSelect, rangeRef, tilesPerRow, tileClassname, inReorderMode, reorderTiles, directSpeaker, localize]);
};
//# sourceMappingURL=TiledLayout.js.map